"use client";

import { c as _c } from "react/compiler-runtime";
import { Stack, TextField, Typography } from "@mui/material";
import Joi from "joi";
import React from "react";
import { getArgumentDisplayLabel } from "#/entities/argument/lib";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { argumentTypeLabels } from "#/entities/argument/model/argumentTypeLabels";
import { caseSlice } from "#/entities/argument/model/caseSlice";
import type { ArgumentObject } from "#/entities/argument/model/types";
import { argumentRenameEndAdornment } from "#/features/argsEditor/ui/ArgumentRenameSuffix";
import { BooleanToggleInput } from "#/shared/ui/atoms/BooleanToggleInput";
import { DebouncedInput } from "#/shared/ui/molecules/DebouncedInput";
import { JsonInput } from "#/shared/ui/molecules/JsonInput";
import { useAppDispatch } from "#/store/hooks";
const validationSchemaMap = {
  [ArgumentType.ARRAY]: Joi.array(),
  [ArgumentType.BINARY_TREE]: Joi.array().items(Joi.number().strict().allow(null)),
  [ArgumentType.LINKED_LIST]: Joi.array().items(Joi.number()),
  [ArgumentType.MATRIX]: Joi.array().items(Joi.array().items(Joi.number().strict(), Joi.string()).messages({
    "array.includes": "Array item at {{#label}} must be either a number or a string"
  }), Joi.string()),
  [ArgumentType.GRAPH]: Joi.array().items(Joi.array().items(Joi.number().strict()))
} as const;
export const ArgInput: React.FC<{
  arg: ArgumentObject;
  parameterNames?: readonly (string | undefined)[];
}> = t0 => {
  const $ = _c(45);
  const {
    arg,
    parameterNames
  } = t0;
  const dispatch = useAppDispatch();
  let t1;
  if ($[0] !== arg || $[1] !== parameterNames) {
    t1 = getArgumentDisplayLabel(arg, parameterNames);
    $[0] = arg;
    $[1] = parameterNames;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const inputLabel = `${t1} (${argumentTypeLabels[arg.type]})`;
  const t2 = arg.label ?? "";
  let t3;
  if ($[3] !== arg.name || $[4] !== t2) {
    t3 = argumentRenameEndAdornment(arg.name, t2);
    $[3] = arg.name;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const renameSuffix = t3;
  let t4;
  if ($[6] !== arg || $[7] !== dispatch) {
    t4 = value => {
      dispatch(caseSlice.actions.updateArgument({
        ...arg,
        input: value
      }));
    };
    $[6] = arg;
    $[7] = dispatch;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleChange = t4;
  switch (arg.type) {
    case ArgumentType.ARRAY:
    case ArgumentType.BINARY_TREE:
    case ArgumentType.LINKED_LIST:
    case ArgumentType.MATRIX:
    case ArgumentType.GRAPH:
      {
        const t5 = validationSchemaMap[arg.type];
        let t6;
        if ($[9] !== arg.input || $[10] !== handleChange || $[11] !== inputLabel || $[12] !== renameSuffix || $[13] !== t5) {
          t6 = <JsonInput label={inputLabel} value={arg.input} onChange={handleChange} validationSchema={t5} size="small" suffixSlot={renameSuffix} timeout={300} />;
          $[9] = arg.input;
          $[10] = handleChange;
          $[11] = inputLabel;
          $[12] = renameSuffix;
          $[13] = t5;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        return t6;
      }
    case ArgumentType.STRING:
      {
        let t5;
        if ($[15] !== renameSuffix) {
          t5 = {
            input: {
              endAdornment: renameSuffix
            }
          };
          $[15] = renameSuffix;
          $[16] = t5;
        } else {
          t5 = $[16];
        }
        let t6;
        if ($[17] !== arg.input || $[18] !== handleChange || $[19] !== inputLabel || $[20] !== t5) {
          t6 = <DebouncedInput label={inputLabel} fullWidth={true} size="small" onChange={handleChange} value={arg.input} timeout={300} slotProps={t5} />;
          $[17] = arg.input;
          $[18] = handleChange;
          $[19] = inputLabel;
          $[20] = t5;
          $[21] = t6;
        } else {
          t6 = $[21];
        }
        return t6;
      }
    case ArgumentType.BOOLEAN:
      {
        let t5;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = {
            width: "100%"
          };
          $[22] = t5;
        } else {
          t5 = $[22];
        }
        let t6;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            alignItems: "center",
            justifyContent: "space-between"
          };
          $[23] = t6;
        } else {
          t6 = $[23];
        }
        let t7;
        if ($[24] !== inputLabel) {
          t7 = <Typography variant="caption" color="text.secondary">{inputLabel}</Typography>;
          $[24] = inputLabel;
          $[25] = t7;
        } else {
          t7 = $[25];
        }
        let t8;
        if ($[26] !== renameSuffix || $[27] !== t7) {
          t8 = <Stack direction="row" spacing={1} sx={t6}>{t7}{renameSuffix}</Stack>;
          $[26] = renameSuffix;
          $[27] = t7;
          $[28] = t8;
        } else {
          t8 = $[28];
        }
        let t9;
        if ($[29] !== arg.input || $[30] !== handleChange) {
          t9 = <BooleanToggleInput value={arg.input} onChange={handleChange} />;
          $[29] = arg.input;
          $[30] = handleChange;
          $[31] = t9;
        } else {
          t9 = $[31];
        }
        let t10;
        if ($[32] !== t8 || $[33] !== t9) {
          t10 = <Stack spacing={0.5} sx={t5}>{t8}{t9}</Stack>;
          $[32] = t8;
          $[33] = t9;
          $[34] = t10;
        } else {
          t10 = $[34];
        }
        return t10;
      }
  }
  const t5 = arg.type === ArgumentType.NUMBER ? "number" : "text";
  let t6;
  if ($[35] !== handleChange) {
    t6 = ev => handleChange(ev.target.value);
    $[35] = handleChange;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== renameSuffix) {
    t7 = {
      input: {
        endAdornment: renameSuffix
      }
    };
    $[37] = renameSuffix;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== arg.input || $[40] !== inputLabel || $[41] !== t5 || $[42] !== t6 || $[43] !== t7) {
    t8 = <TextField label={inputLabel} value={arg.input} type={t5} fullWidth={true} size="small" onChange={t6} slotProps={t7} />;
    $[39] = arg.input;
    $[40] = inputLabel;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  return t8;
};